
import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, RefreshCw, Quote, History, Play, Pause, SkipForward, SkipBack } from 'lucide-react';
import { fetchDailyIntel } from '../services/almanacService';
import { AlmanacEvent } from '../types';

const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
];

const Almanac: React.FC = () => {
  // State per la data corrente selezionata
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState<AlmanacEvent[]>([]);
  const [loading, setLoading] = useState(false);
  
  // State per il "fatto" principale visualizzato
  const [mainEventIndex, setMainEventIndex] = useState(0);
  
  // State per il Player
  const [isPlaying, setIsPlaying] = useState(false);

  // Caricamento dati al cambio data
  useEffect(() => {
    loadEventsForDate(currentDate);
  }, [currentDate]);

  // Logica Timer 7 Secondi
  useEffect(() => {
    let interval: any;
    if (isPlaying && events.length > 1) {
        interval = setInterval(() => {
            setMainEventIndex((prev) => (prev + 1) % events.length);
        }, 7000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, events.length]);

  const loadEventsForDate = async (date: Date) => {
    setLoading(true);
    setIsPlaying(false); // Reset player quando cambio giorno
    setEvents([]);
    
    const day = date.getDate();
    const month = date.getMonth();
    
    // Fetch data
    const results = await fetchDailyIntel(month, day);
    setEvents(results);
    setMainEventIndex(0);
    setLoading(false);
  };

  const changeDate = (days: number) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + days);
    setCurrentDate(newDate);
  };

  // Navigazione interna agli eventi (stesso giorno)
  const nextEvent = () => {
      if (events.length > 1) setMainEventIndex((prev) => (prev + 1) % events.length);
  };

  const prevEvent = () => {
      if (events.length > 1) setMainEventIndex((prev) => (prev - 1 + events.length) % events.length);
  };

  const handleDateInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if(e.target.value) {
        setCurrentDate(new Date(e.target.value));
    }
  };

  // Formattazione data input (YYYY-MM-DD)
  const inputDateValue = currentDate.toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-[#f4f4f4] text-black pt-32 pb-20 font-sans flex flex-col items-center justify-center relative">
        
        {/* BACKGROUND PATTERN */}
        <div className="absolute inset-0 pointer-events-none opacity-5" style={{backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '20px 20px'}}></div>

        {/* CONTAINER CENTRALE */}
        <div className="w-full max-w-2xl px-4 z-10">
            
            {/* HEADER MINIMAL */}
            <div className="text-center mb-8 animate-fade-in">
                <div className="flex items-center justify-center gap-2 text-xs font-bold tracking-[0.3em] uppercase text-gray-400 mb-2">
                    <History size={14} /> Archivio Storico
                </div>
                <h1 className="text-4xl font-serif font-bold tracking-tight">L'Almanacco</h1>
            </div>

            {/* CARD CALENDARIO A STRAPPO */}
            <div className="bg-white shadow-xl border-t-8 border-black relative overflow-hidden transition-all duration-500 min-h-[600px] flex flex-col">
                
                {/* 1. SEZIONE DATA (HEADER CALENDARIO) */}
                <div className="bg-gray-50 border-b border-gray-100 p-6 flex items-center justify-between">
                    <button onClick={() => changeDate(-1)} className="p-2 hover:bg-black hover:text-white transition-colors rounded-full group">
                        <ChevronLeft size={24} className="group-hover:-translate-x-1 transition-transform" />
                    </button>
                    
                    <div className="text-center">
                        <div className="text-sm font-bold uppercase tracking-widest text-red-600 mb-1">
                            {currentDate.toLocaleDateString('it-IT', { weekday: 'long' })}
                        </div>
                        <div className="text-5xl md:text-6xl font-serif font-black leading-none">
                            {currentDate.getDate()}
                        </div>
                        <div className="text-xl font-light text-gray-500 uppercase tracking-widest mt-1">
                            {MONTHS_IT[currentDate.getMonth()]}
                        </div>
                    </div>

                    <button onClick={() => changeDate(1)} className="p-2 hover:bg-black hover:text-white transition-colors rounded-full group">
                        <ChevronRight size={24} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>

                {/* 2. CONTENUTO PRINCIPALE (FATTO DEL GIORNO) */}
                <div className="flex-1 p-8 md:p-12 flex flex-col items-center justify-center text-center relative">
                    
                    {loading ? (
                        <div className="flex flex-col items-center gap-4 animate-pulse">
                            <RefreshCw className="animate-spin text-gray-300" size={32} />
                            <div className="text-xs font-bold uppercase tracking-widest text-gray-400">Ricerca Archivi Profondi...</div>
                            <div className="text-[10px] text-gray-500 font-serif italic text-center max-w-xs leading-relaxed">
                                "Attendere prego. Stiamo viaggiando indietro nel tempo per recuperare le fonti originali."
                            </div>
                        </div>
                    ) : events.length > 0 ? (
                        <div className="animate-slide-up max-w-lg w-full">
                            <div className="absolute top-6 left-1/2 -translate-x-1/2 text-[120px] leading-none font-serif text-gray-100 select-none z-0">
                                {events[mainEventIndex].year}
                            </div>
                            
                            <div className="relative z-10">
                                <span className="inline-block px-3 py-1 mb-6 text-[10px] font-bold uppercase tracking-widest border border-black bg-black text-white">
                                    {events[mainEventIndex].category.replace('_', ' ')}
                                </span>
                                
                                <h2 className="text-3xl md:text-4xl font-serif font-bold leading-tight mb-6">
                                    {events[mainEventIndex].title}
                                </h2>

                                <div className="w-12 h-1 bg-red-600 mx-auto mb-6"></div>

                                <p className="text-lg text-gray-600 font-light leading-relaxed mb-8 font-serif italic min-h-[80px]">
                                    "{events[mainEventIndex].description}"
                                </p>

                                <div className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center justify-center gap-2 mb-8">
                                    Fonte: {events[mainEventIndex].source}
                                </div>
                            </div>
                            
                            {/* CONTROLLI PLAYER EVENTI (Se ci sono più eventi) */}
                            {events.length > 1 && (
                                <div className="relative z-20 flex items-center justify-center gap-4 border-t border-gray-100 pt-6 mt-4">
                                    
                                    <button 
                                        onClick={prevEvent} 
                                        className="p-2 text-gray-400 hover:text-black hover:bg-gray-100 rounded-full transition-colors"
                                        title="Evento Precedente"
                                    >
                                        <SkipBack size={18} />
                                    </button>

                                    <div className="flex flex-col items-center min-w-[100px]">
                                        <span className="text-xs font-bold tracking-widest text-black">
                                            {mainEventIndex + 1} / {events.length}
                                        </span>
                                        <span className="text-[9px] uppercase text-gray-400">Voci Disponibili</span>
                                    </div>

                                    <button 
                                        onClick={nextEvent} 
                                        className="p-2 text-gray-400 hover:text-black hover:bg-gray-100 rounded-full transition-colors"
                                        title="Evento Successivo"
                                    >
                                        <SkipForward size={18} />
                                    </button>

                                    <div className="w-px h-8 bg-gray-200 mx-2"></div>

                                    <button 
                                        onClick={() => setIsPlaying(!isPlaying)} 
                                        className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest rounded transition-all ${
                                            isPlaying ? 'bg-black text-white' : 'bg-gray-100 text-black hover:bg-gray-200'
                                        }`}
                                    >
                                        {isPlaying ? (
                                            <><Pause size={12} fill="currentColor" /> Pause</>
                                        ) : (
                                            <><Play size={12} fill="currentColor" /> Play (7s)</>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="text-gray-400 italic font-serif">
                            <Quote size={48} className="mx-auto mb-4 opacity-20" />
                            Nessun evento critico registrato per questa data negli archivi.
                            <br/>La storia è ancora da scrivere.
                        </div>
                    )}
                </div>

                {/* 3. FOOTER (RICERCA SECONDARIA) */}
                <div className="bg-gray-50 border-t border-gray-100 p-4 flex justify-between items-center text-xs">
                    <div className="flex items-center gap-2 text-gray-500">
                        <span className="font-bold">ACCADDE OGGI</span>
                        <span className="hidden sm:inline"> | Archivio Cyberumanità</span>
                    </div>
                    
                    <div className="relative group">
                        <div className="flex items-center gap-2 cursor-pointer hover:text-black transition-colors text-gray-500 font-bold uppercase tracking-wide">
                             <CalendarIcon size={14} />
                             <span>Cerca altra data</span>
                        </div>
                        {/* Date Input Overlay */}
                        <input 
                            type="date" 
                            value={inputDateValue}
                            onChange={handleDateInput}
                            className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                        />
                    </div>
                </div>

            </div>
            
            {/* SHADOW DECORATION */}
            <div className="w-[95%] mx-auto h-4 bg-gray-300 rounded-b-lg opacity-50 blur-sm"></div>

        </div>
    </div>
  );
};

export default Almanac;
